import DarkModeOutlined from "@mui/icons-material/DarkModeOutlined";
import LightModeOutlined from "@mui/icons-material/LightModeOutlined";
import AppBar from "@mui/material/AppBar";
import Avatar from "@mui/material/Avatar";
import FormControl from "@mui/material/FormControl";
import IconButton from "@mui/material/IconButton";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import Stack from "@mui/material/Stack";
import Toolbar from "@mui/material/Toolbar";
import Typography from "@mui/material/Typography";
import { useTheme } from "@mui/material/styles";
import { useGetIdentity, useGetLocale, useSetLocale, useTranslate } from "@refinedev/core";
import { HamburgerMenu, RefineThemedLayoutV2HeaderProps } from "@refinedev/mui";
import i18n from "i18next";
import React, { useContext } from "react";

import { PROJECT_VERSION } from "@/Environment";
import { ColorModeContext } from "@/contexts/color-mode";
import { EnvironmentIndicator } from "../information/EnvironmentIndicator";

type IUser = {
  id: number;
  name: string;
  avatar: string;
};

export interface HeaderProps extends RefineThemedLayoutV2HeaderProps {
  children?: React.ReactNode;
}

export const Header: React.FC<HeaderProps> = ({ children, sticky = true }) => {
  const { mode, setMode } = useContext(ColorModeContext);
  const theme = useTheme();
  const isDarkMode = mode === "dark";

  const { data: user } = useGetIdentity<IUser>();

  const changeLanguage = useSetLocale();
  const locale = useGetLocale();
  const currentLocale = locale();
  const t = useTranslate();

  return (
    <AppBar
      position={sticky ? "sticky" : "relative"}
      elevation={0}
      sx={{
        bgcolor: isDarkMode ? "#212121" : "#f2f2f2",
        color: isDarkMode ? theme.palette.common.white : theme.palette.text.primary,
        borderBottom: 1,
        borderColor: "divider",
      }}
    >
      <Toolbar>
        <Stack direction="row" width="100%" alignItems="center">
          <HamburgerMenu />
          <Stack direction="row" width="100%" justifyContent="flex-start" alignItems="center" gap="16px">
            <img src={`/images/logo-${mode === "dark" ? "light" : "dark"}.svg`} height="30" width="160" />
            <Typography>
              {t("product.short-title", "Permissions Manager")} - <span>{PROJECT_VERSION}</span>
            </Typography>

            {children}
          </Stack>
          <Stack direction="row" width="100%" justifyContent="flex-end" alignItems="center" gap="16px">
            <EnvironmentIndicator />
            <FormControl sx={{ minWidth: 64 }}>
              <Select
                disableUnderline
                defaultValue={currentLocale}
                inputProps={{ "aria-label": "Without label" }}
                variant="standard"
                sx={{
                  color: "inherit",
                  "& .MuiSvgIcon-root": {
                    color: "inherit",
                  },
                  "& .MuiStack-root > .MuiTypography-root": {
                    display: {
                      xs: "none",
                      sm: "block",
                    },
                  },
                }}
              >
                {[...(i18n.languages ?? [])].sort().map((lang: string) => (
                  // @ts-ignore
                  <MenuItem
                    selected={currentLocale === lang}
                    key={lang}
                    defaultValue={lang}
                    onClick={() => {
                      changeLanguage(lang);
                    }}
                    value={lang}
                  >
                    <Stack direction="row" alignItems="center" justifyContent="center">
                      <Typography>{lang}</Typography>
                    </Stack>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <IconButton
              color="inherit"
              onClick={() => {
                setMode();
              }}
            >
              {mode === "dark" ? <LightModeOutlined /> : <DarkModeOutlined />}
            </IconButton>
            {(user?.avatar || user?.name) && (
              <Stack direction="row" gap="16px" alignItems="center" justifyContent="center">
                {user?.name && (
                  <Typography
                    sx={{
                      display: {
                        xs: "none",
                        sm: "inline-block",
                      },
                    }}
                    variant="subtitle2"
                  >
                    {user?.name}
                  </Typography>
                )}
                <Avatar src={user?.avatar} alt={user?.name} />
              </Stack>
            )}
          </Stack>
        </Stack>
      </Toolbar>
    </AppBar>
  );
};
